import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import OrgSecurityPolicySection from '@/components/OrgSecurityPolicySection';
import { AuthorizedAgents } from '@/components/account/AuthorizedAgents';
import { OrgNotificationRouting } from '@/components/notifications/OrgNotificationRouting.tsx';
import { PsaConnections } from '@/components/psa/PsaConnections.tsx';
// SaaS-only sections, imported via feature barrels so the trial build swaps them
// for teasers (and excludes their CM/approval/maintenance code).
import { OrgChangeManagementSection } from '@/features/changeManagement';
import { MaintenanceWindowsSection } from '@/features/maintenanceWindows';
import { ApprovalPoliciesSection } from '@/features/approvalPolicies';

/**
 * The pages of Organization Admin that are a header plus existing sections.
 *
 * The organization's settings used to be one long page, and the VPN and
 * notification areas each carried an "Organization" group of their own. Everything
 * that configures the organization is now under `/admin`, a page per topic; a
 * feature's own area in the main nav keeps what is yours and what you do day to day.
 */

const card = 'bg-card border border-border rounded-lg p-5';

export function NetworkDefaultsPage() {
  return (
    <PageContainer width="narrow" className="space-y-5 md:space-y-6">
      <PageHeader
        title="Network defaults"
        subtitle="Where every port starts, before a region, device or port says otherwise"
      />
      <section className={card}>
        <OrgSecurityPolicySection />
      </section>
    </PageContainer>
  );
}

export function ChangeManagementPage() {
  return (
    <PageContainer width="medium" className="space-y-5 md:space-y-6">
      <PageHeader
        title="Change management"
        subtitle="Whether approved changes may start on their own, and who has to approve them"
      />
      <section className={card}>
        <OrgChangeManagementSection />
      </section>
      <section className={card}>
        <ApprovalPoliciesSection />
      </section>
    </PageContainer>
  );
}

export function MaintenanceWindowsPage() {
  return (
    <PageContainer width="medium" className="space-y-5 md:space-y-6">
      <PageHeader title="Maintenance windows" subtitle="Quiet periods for planned work" />
      <section className={card}>
        <MaintenanceWindowsSection />
      </section>
    </PageContainer>
  );
}

export function AiAssistantsPage() {
  return (
    <PageContainer width="medium" className="space-y-5 md:space-y-6">
      <PageHeader
        title="AI assistants"
        subtitle="The assistants people have connected to this organization"
      />
      {/* Unwrapped: AuthorizedAgents draws its own card. The server decides which rows
          an administrator may see, per grant, from their reach over its context — so a
          division administrator sees their own division's agents and not another's. */}
      <AuthorizedAgents scope="all" />
    </PageContainer>
  );
}

export function OrgRoutingPage() {
  return (
    <PageContainer width="medium">
      <PageHeader
        title="Notification routing"
        subtitle="Where this organization’s notifications go, independently of anyone’s personal rules"
      />
      <OrgNotificationRouting show="routes" />
    </PageContainer>
  );
}

export function OrgDestinationsPage() {
  return (
    <PageContainer width="medium">
      <PageHeader
        title="Shared destinations"
        subtitle="Addresses and webhooks the organization’s routes can point at — not personal rules"
      />
      <OrgNotificationRouting show="destinations" />
    </PageContainer>
  );
}

export function PsaPage() {
  return (
    <PageContainer width="medium">
      <PageHeader
        title="PSA ticketing"
        subtitle="Incidents as tickets in your PSA, kept in step: notes, acknowledgement, priority and recovery"
      />
      <PsaConnections />
    </PageContainer>
  );
}
